/**
 * ResizeHandle Hook
 * Handles drag-to-resize functionality for the dev tool panel
 * 
 * Features:
 * - Mouse and touch support
 * - Smooth dragging with visual feedback
 * - Min/max height constraints
 * - Double-click to cycle through preset heights
 * - Emits height changes via custom events
 */

const ResizeHandle = {
  mounted() {
    this.isResizing = false;
    this.startY = 0;
    this.startHeight = 0;
    this.currentHeight = 0;
    
    // Configuration
    this.minHeight = 100;
    this.maxHeight = window.innerHeight * 0.9;
    this.presetHeights = [250, 400, window.innerHeight * 0.6];
    this.lastClickTime = 0;
    this.doubleClickDelay = 300;
    
    // Get the panel element (parent of resize handle)
    this.panel = this.el.closest('[data-panel]');
    if (!this.panel) {
      console.error('ResizeHandle: Could not find panel element');
      return;
    }
    
    // Bind methods
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleDoubleClick = this.handleDoubleClick.bind(this);
    
    // Add event listeners
    this.el.addEventListener('mousedown', this.handleMouseDown);
    this.el.addEventListener('touchstart', this.handleTouchStart, { passive: false });
    
    // Add cursor style
    this.el.style.cursor = 'ns-resize';
  },
  
  handleMouseDown(e) {
    e.preventDefault();
    this.startResize(e.clientY);
    
    // Check for double-click
    const currentTime = Date.now();
    if (currentTime - this.lastClickTime < this.doubleClickDelay) {
      this.handleDoubleClick();
    }
    this.lastClickTime = currentTime;
  },
  
  handleTouchStart(e) {
    e.preventDefault();
    if (e.touches.length === 1) {
      this.startResize(e.touches[0].clientY);
    }
  },
  
  startResize(clientY) {
    this.isResizing = true;
    this.startY = clientY;
    this.startHeight = this.panel.offsetHeight;
    
    // Add resize class for visual feedback
    this.panel.classList.add('resizing');
    document.body.style.userSelect = 'none';
    
    // Add document listeners
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
    document.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    document.addEventListener('touchend', this.handleTouchEnd);
  },
  
  handleMouseMove(e) {
    if (!this.isResizing) return;
    this.updateHeight(e.clientY);
  },
  
  handleTouchMove(e) {
    if (!this.isResizing || e.touches.length !== 1) return;
    e.preventDefault();
    this.updateHeight(e.touches[0].clientY);
  },
  
  updateHeight(clientY) {
    const deltaY = this.startY - clientY;
    let newHeight = this.startHeight + deltaY;
    
    // Apply constraints
    newHeight = Math.max(this.minHeight, Math.min(newHeight, this.maxHeight));
    this.currentHeight = newHeight;
    
    // Update panel height
    this.panel.style.height = `${newHeight}px`;
    
    // Emit resize event for other components to react
    this.el.dispatchEvent(new CustomEvent('panel-resizing', {
      bubbles: true,
      detail: { height: newHeight }
    }));
  },
  
  handleMouseUp(e) {
    if (!this.isResizing) return;
    this.stopResize();
  },
  
  handleTouchEnd(e) {
    if (!this.isResizing) return;
    this.stopResize();
  },
  
  stopResize() {
    this.isResizing = false;
    
    // Remove resize class
    this.panel.classList.remove('resizing');
    document.body.style.userSelect = '';
    
    // Remove document listeners
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
    document.removeEventListener('touchmove', this.handleTouchMove);
    document.removeEventListener('touchend', this.handleTouchEnd);
    
    // Emit final height
    this.el.dispatchEvent(new CustomEvent('panel-resized', {
      bubbles: true,
      detail: { height: this.currentHeight || this.panel.offsetHeight }
    }));
  },
  
  handleDoubleClick() {
    // Find the next preset height
    const currentHeight = this.panel.offsetHeight;
    let nextHeight = this.presetHeights[0];
    
    for (let i = 0; i < this.presetHeights.length; i++) {
      if (currentHeight < this.presetHeights[i] - 10) {
        nextHeight = this.presetHeights[i];
        break;
      } else if (i === this.presetHeights.length - 1) {
        // If we're at or above the largest preset, go back to the smallest
        nextHeight = this.presetHeights[0];
      }
    }
    
    // Animate to the new height
    this.panel.style.transition = 'height 0.3s ease';
    this.panel.style.height = `${nextHeight}px`;
    
    // Emit the height change
    setTimeout(() => {
      this.panel.style.transition = '';
      this.el.dispatchEvent(new CustomEvent('panel-resized', {
        bubbles: true,
        detail: { height: nextHeight, preset: true }
      }));
    }, 300);
  },
  
  destroyed() {
    // Clean up event listeners
    this.el.removeEventListener('mousedown', this.handleMouseDown);
    this.el.removeEventListener('touchstart', this.handleTouchStart);
    
    // Make sure to clean up any active resize
    if (this.isResizing) {
      this.stopResize();
    }
  }
};

export default ResizeHandle;